import * as React from 'react';

import { Box } from '@mui/material';

import PageTitle from '../../components/PageTitle/PageTitle';
import ReservationsTable from '../../components/Tables/Reservation/Reservation';
import { enableShopifyVenueID } from '../../config';
import { BusinessMonthlyReservationLimit } from '../../components/BusinessMonthlyReservationLimit/BusinessMonthlyReservationLimit';
import { useAppSelector } from '../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { useReservationLimitInPeriodContext } from '../../contexts/ReservationsLimitInPeriodContext';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import { brandPromotionLimit, getVenueReservationLimit } from '../../utils/reservations';
import { OfferCategory } from '../../graphql/API';
import { Plan } from '../SubscriptionPlans/types';

const Reservation: React.FC = () => {
  const [tableHeight, setTableHeight] = React.useState(298);
  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const { totalCompletedPromotions, totalReservedAndCheckedInReservations } =
    useReservationLimitInPeriodContext() || {};

  const handleTableHeight = (val: number) => {
    setTableHeight(val);
  };

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) || false;
  const isBusinessVenue = venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans) || false;

  const totalReservations = React.useMemo(
    () => (isOnlineBrand ? totalCompletedPromotions : totalReservedAndCheckedInReservations) || 0,
    [isOnlineBrand, totalReservedAndCheckedInReservations, totalCompletedPromotions],
  );

  const getPromotionLimit = React.useCallback(
    (plan: Plan | undefined): number => {
      if (isBusinessVenue) {
        return getVenueReservationLimit(venueState?.type as OfferCategory, plan) ?? 0;
      }
      return brandPromotionLimit[plan || ''] ?? 0;
    },
    [isBusinessVenue, venueState?.type],
  );

  return (
    <Box sx={{ backgroundColor: 'white', flex: 1, p: 4 }}>
      <PageTitle title='Reservations' />

      {enableShopifyVenueID && String(venueState?.id) === String(enableShopifyVenueID) ? null : (
        <Box sx={{ py: 2.2 }}>
          <BusinessMonthlyReservationLimit
            totalReservations={totalReservations}
            plan={subscriptionPlan}
            limit={getPromotionLimit(subscriptionPlan)}
            fitContent
          />
        </Box>
      )}

      <Box mt={2}>
        <ReservationsTable
          height={`calc(100vh - ${tableHeight}px)`}
          detail={true}
          filters={{
            sortBy: 'createdAt',
            sortDirection: 'desc',
          }}
          handleTableHeight={handleTableHeight}
        />
      </Box>
    </Box>
  );
};

export default Reservation;
